/**
 * 系统更新公告详情页
 */

import type { ReactElement } from 'react'
import { Link, useParams } from 'react-router-dom'
import { ArrowLeft, Calendar, Loader2, Pin } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { SystemUpdateMarkdown } from '@/components/SystemUpdateMarkdown'
import { useSystemUpdateDetail } from '@/hooks/useSystemUpdates'

/**
 * 格式化日期时间
 * @param value ISO 时间字符串
 * @returns 格式化后的时间
 */
function formatDateTime(value: string): string {
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) {
    return value
  }
  return date.toLocaleString('zh-CN', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  })
}

/**
 * 系统更新公告详情页
 * @returns ReactElement
 */
export function SystemUpdateDetailPage(): ReactElement {
  const params = useParams<{ updateId: string }>()
  const updateId = params.updateId

  const {
    data: update,
    isLoading,
    error,
  } = useSystemUpdateDetail(updateId)

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
        <span className="ml-2 text-muted-foreground">加载公告详情中...</span>
      </div>
    )
  }

  if (error !== null) {
    return (
      <div className="rounded-xl border bg-card p-10 text-center">
        <h2 className="text-lg font-semibold">公告加载失败</h2>
        <p className="mt-2 text-sm text-destructive">{error.message}</p>
        <Button asChild className="mt-4">
          <Link to="/updates">返回更新列表</Link>
        </Button>
      </div>
    )
  }

  if (update === undefined) {
    return (
      <div className="rounded-xl border bg-card p-10 text-center">
        <h2 className="text-lg font-semibold">公告不存在</h2>
        <Button asChild className="mt-4">
          <Link to="/updates">返回更新列表</Link>
        </Button>
      </div>
    )
  }

  return (
    <article className="mx-auto max-w-4xl space-y-6">
      <div className="flex items-center justify-between gap-3">
        <Button asChild variant="outline" size="sm">
          <Link to="/updates">
            <ArrowLeft className="h-4 w-4" />
            返回更新列表
          </Link>
        </Button>
        {update.isPinned ? (
          <span className="inline-flex items-center gap-1 rounded-full bg-amber-500/10 px-2 py-0.5 text-xs text-amber-700">
            <Pin className="h-3.5 w-3.5" />
            置顶公告
          </span>
        ) : null}
      </div>

      <header className="space-y-3 rounded-xl border bg-card p-6">
        <h1 className="text-2xl font-bold leading-9 tracking-tight">{update.title}</h1>
        <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
          <Calendar className="h-4 w-4" />
          发布时间：{formatDateTime(update.publishedAt)}
        </div>
        {update.summary !== undefined && update.summary.trim() !== '' ? (
          <p className="text-sm text-muted-foreground">{update.summary}</p>
        ) : null}
      </header>

      <section className="rounded-xl border bg-card p-6">
        <SystemUpdateMarkdown content={update.contentMd} />
      </section>
    </article>
  )
}
